import { useTranslation } from "react-i18next";
import { useQuery } from "react-query";

import { useJsonApi } from "@/modules/api/hooks/useJsonApi";
import { queryKeys } from "@/modules/api/queryKeys";
import { apiErrorValidate } from "@/modules/api/utils/apiErrorValidate";
import { useAuthContext } from "@/modules/auth/hooks/useAuthContext";
import { getTutorialProgressQuery } from "@/modules/tutorials/api/queries/tutorialProgress/getTutorialProgressQuery";

import { parseTutorialProgress } from "./parseTutorialProgress";

const translationNamespace = "apiMessages/useGetTutorialProgressDetailsQuery";

export function useGetTutorialProgressDetailsQuery(
  tutorialId: string | undefined
) {
  const jsonApi = useJsonApi();
  const { t } = useTranslation(translationNamespace);

  const { userCompanyId } = useAuthContext();
  return useQuery(
    userCompanyId && tutorialId
      ? {
          queryKey: [
            queryKeys.tutorials.getTutorialProgressDetails,
            userCompanyId,
            tutorialId,
          ],
          queryFn: () =>
            getTutorialProgressQuery(jsonApi, {
              companyId: userCompanyId,
              tutorialId,
            }),
          retry: false,
          refetchOnWindowFocus: false,
          select: (response) => {
            if (response) {
              return parseTutorialProgress(response);
            }
          },
          onError: (error) => {
            apiErrorValidate(error, {
              401: {
                UNAUTHORIZED: t(
                  "You are not allowed to view the tutorial. Please sign in again"
                ),
              },
              403: {
                ACCESS_FORBIDDEN: t(
                  "You don't have an access to view the tutorial"
                ),
              },
              404: {
                TUTORIAL_NOT_FOUND: t("The tutorial cannot be found"),
              },
            });
          },
        }
      : { enabled: false }
  );
}
